/**
 * SchoolCampusesPage — full-page campus management surface.
 * Reuses CampusesCard (CRUD + broadcast) and adds a per-school
 * "Campus-scoped portals" toggle that enforces campus isolation on
 * student/teacher/parent portals when turned on.
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Building2, Users, Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';
import CampusesCard from '@/components/school/CampusesCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SchoolCampusesPage({ tenantId }) {
  const qc = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['school-settings', tenantId],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/school/tenants/${tenantId}/settings`, { headers: authHeaders() });
      if (!r.ok) return {};
      return r.json();
    },
    enabled: !!tenantId,
  });

  const { data: campusesData } = useQuery({
    queryKey: ['campuses', tenantId],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/school/tenants/${tenantId}/campuses`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Failed');
      return r.json();
    },
    enabled: !!tenantId,
  });
  const campuses = campusesData?.items || [];

  const scopingEnabled = settings?.campus_scoping_enabled === true;

  const saveScoping = useMutation({
    mutationFn: async (v) => {
      const r = await fetch(`${API_URL}/api/school/tenants/${tenantId}/settings`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ campus_scoping_enabled: v }),
      });
      if (!r.ok) throw new Error('Failed to save');
      return r.json();
    },
    onSuccess: (_, v) => {
      toast.success(v ? 'Campus-scoped portals ON' : 'Campus-scoped portals OFF');
      qc.invalidateQueries({ queryKey: ['school-settings', tenantId] });
    },
    onError: (e) => toast.error(e.message),
  });

  if (isLoading) {
    return <div className="flex items-center justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="school-campuses-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-indigo-500" /> Campuses
          </h2>
          <p className="text-sm text-slate-500">Manage physical, BYOD, online, and satellite campuses under this school. Campuses can share one timetable/academic year or own their own.</p>
        </div>
        <div className="flex gap-2">
          <Badge variant="outline">{campuses.length} campus{campuses.length === 1 ? '' : 'es'}</Badge>
          {scopingEnabled && <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100">Scoped portals ON</Badge>}
        </div>
      </div>

      {/* Campus-scoping toggle — controls portal isolation */}
      <Card data-testid="campus-scoping-card" className="border-indigo-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4 text-indigo-500" /> Campus-scoped portals
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <Label htmlFor="campus-scoping-switch" className="text-sm cursor-pointer">
                Show students, teachers & parents only content from their own campus
              </Label>
              <p className="text-xs text-slate-500 mt-0.5">
                When ON: a teacher assigned to "Main Campus" sees only that campus's classes, students and timetable on their portal.
                Students & parents see only their campus's timetable, announcements and classes.
                When OFF (default): everyone sees tenant-wide content — useful for single-campus schools or when you want centralised visibility.
              </p>
            </div>
            <Switch
              id="campus-scoping-switch"
              checked={scopingEnabled}
              onCheckedChange={(v) => saveScoping.mutate(v)}
              disabled={saveScoping.isPending}
              data-testid="campus-scoping-switch"
            />
          </div>
          {scopingEnabled && campuses.length === 0 && (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800 flex items-center gap-2">
              <Users className="h-3.5 w-3.5" /> You have scoping ON but no campuses yet. Add one below to make scoping take effect.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reuse the existing CampusesCard for CRUD + broadcast */}
      <CampusesCard tenantId={tenantId} />
    </div>
  );
}
